import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  UpdateProjectVersionByIdParams,
  UpdateProjectVersionByIdBody,
  UpdateProjectVersionByIdData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface UpdateProjectVersionByIdState
  extends MergeRequestState<UpdateProjectVersionByIdData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.updateProjectVersionById,
  isFetching: (state: ReduxState) =>
    state.server.updateProjectVersionById.isFetching,
  data: (state: ReduxState) => state.server.updateProjectVersionById.data,
  error: (state: ReduxState) => state.server.updateProjectVersionById.error,
};

const initialState: UpdateProjectVersionByIdState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/updateProjectVersionById';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      params,
      body,
      config,
    }: {
      params: UpdateProjectVersionByIdParams;
      body: UpdateProjectVersionByIdBody;
      config?: ServerApiConfig;
    },
    { rejectWithValue },
  ) =>
    serverApi
      .updateProjectVersionById(params, body, config)
      .catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerUpdateProjectVersionById {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const updateProjectVersionById: ServerUpdateProjectVersionById = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
